import { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { WBSButton } from '@/common/WBSButton';
import { CheckboxLabel, WBSCheckbox } from '@/common/WBSCheckbox';
import { Panel, PanelContent } from '~/styles/tables/filterandSettingsStyles';
import type { Column } from './WbsTable';

interface ColumnSettingsPanelProps<T> {
  isOpen: boolean;
  columns: Column<T>[];
  visibleColumns: Set<keyof T>;
  onToggleColumn: (columnKey: keyof T) => void;
  onResetDefault: () => void;
}

function ColumnSettingsPanel<T extends Record<string, any>>({
  isOpen,
  columns,
  visibleColumns,
  onToggleColumn,
  onResetDefault,
}: ColumnSettingsPanelProps<T>) {
  const { t } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState('0px');

  useEffect(() => {
    if (isOpen && contentRef.current) {
      setMaxHeight(`${contentRef.current.scrollHeight}px`);
    } else {
      setMaxHeight('0px');
    }
  }, [isOpen]);

  return (
    <Panel isOpen={isOpen} panelType="settings" maxHeight={maxHeight}>
      <PanelContent ref={contentRef}>
        <SettingsTitle>{t('wbs.header-options')}</SettingsTitle>
        <SettingsGrid>
          {columns.map((column) => (
            <CheckboxLabel key={String(column.key)}>
              <WBSCheckbox
                checked={visibleColumns.has(column.key)}
                onChange={() => onToggleColumn(column.key)}
              />
              {column.title}
            </CheckboxLabel>
          ))}
        </SettingsGrid>
        <div style={{ textAlign: 'center', width: '100%' }}>
          <WBSButton
            wide
            style={{ marginTop: '1.5rem' }}
            onClick={onResetDefault}
          >
            {t('wbs.reset-default')}
          </WBSButton>
        </div>
      </PanelContent>
    </Panel>
  );
}

export default memo(ColumnSettingsPanel) as typeof ColumnSettingsPanel;

const SettingsTitle = styled.h3`
  color: var(--black);
  font-size: 16px;
  font-weight: 500;
  margin: 0.2rem 0 1.8rem 0;
  text-align: center;
`;

const SettingsGrid = styled.div`
  align-items: start;
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: repeat(2, minmax(0, 120px));
  grid-template-rows: repeat(6, auto);
  justify-content: center;
  margin: 0 auto;
  row-gap: 0.5rem;
  width: fit-content;
`;
